import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from '../../services/login.service';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { LoginModel, LoginCredentialModel } from '../../models/login.model';
import { CommonMethodService } from '../../common/commonMethod.service';
import { MessagingService } from '../../services/messaging.service';
import { UserService } from '../../services/user.service';
import { ThemeService } from '../../services/theme.service';

@Component({
  selector: 'app-dashboard',
  templateUrl: 'login.component.html',
  providers: [LoginService, CommonMethodService]
})
export class LoginComponent implements OnInit, OnDestroy {

  subscribe: Array<Subscription> = [];
  loginLogo: string = 'assets/Icon-256.png';
  constructor(private toaster: ToastrService, private route: Router, private loginService: LoginService,
    private commonMethod: CommonMethodService, private messagingService: MessagingService, private userService: UserService,
    private themeService: ThemeService) {
      this.updateLoginLogo(this.themeService.current);
      this.subscribe.push(this.themeService.theme$.subscribe(theme => this.updateLoginLogo(theme)));
    }

  loginForm: FormGroup;
  fcmToken: string;
  loader: boolean = false;
  shakeError = false;
  showSuccessOverlay = false;
  welcomeName = '';
  welcomeRole = '';
  welcomeBgImage = '';
  accountImageUrl = '';
  lastLoginDisplay: string | null = null;

  private static readonly LAST_LOGIN_KEY = 'vw-last-login-map';

  // "Remember me" has two parts: (1) the username is recalled and pre-filled
  // on this browser next time (purely client-side convenience, cleared the
  // moment the box is unchecked), and (2) the flag is sent to the backend,
  // which already supports issuing a longer-lived token for it
  // (UserJWTController/TokenProvider) — the frontend just never exposed it.
  private static readonly REMEMBERED_USERNAME_KEY = 'vw-remembered-username';

  private static readonly ROLE_LABELS: { [role: string]: string } = {
    SUPER_ADMIN: 'Super Admin',
    ACCOUNT_ADMIN: 'Account Admin',
    WEB_ADMIN: 'Web Admin',
    DEVICE_ADMIN: 'Device Admin',
    EMPLOYEE_ADMIN: 'Employee Admin',
    RESIDENT_ADMIN: 'Resident Admin',
    FAMILY: 'Family',
  };

  private static readonly ROLE_BG_IMAGES: { [role: string]: string } = {
    SUPER_ADMIN: 'v1.jpg',
    ACCOUNT_ADMIN: 'vehicle_check_in.jpg',
    WEB_ADMIN: 'web1.jpg',
    DEVICE_ADMIN: 'v3.jpg',
    EMPLOYEE_ADMIN: 'staff.jpg',
    RESIDENT_ADMIN: 'vehicle_check_in.jpg',
  };

  ngOnInit(): void {
    const rememberedUsername = this.getRememberedUsername();
    this.loginForm = new FormGroup({
      username: new FormControl(rememberedUsername || '', [Validators.required]),
      password: new FormControl('', [Validators.required]),
      rememberMe: new FormControl(!!rememberedUsername)
    });
    this.loginForm.controls.username.valueChanges.subscribe(username => {
      this.lastLoginDisplay = this.getLastLogin(username);
    });
    if (rememberedUsername) {
      this.lastLoginDisplay = this.getLastLogin(rememberedUsername);
    }
  }

  private getRememberedUsername(): string | null {
    try {
      return localStorage.getItem(LoginComponent.REMEMBERED_USERNAME_KEY);
    } catch {
      return null;
    }
  }

  private setRememberedUsername(username: string, remember: boolean): void {
    try {
      if (remember && username) {
        localStorage.setItem(LoginComponent.REMEMBERED_USERNAME_KEY, username);
      } else {
        localStorage.removeItem(LoginComponent.REMEMBERED_USERNAME_KEY);
      }
    } catch {
      // localStorage unavailable (private mode, etc.) — skips silently
    }
  }

  private updateLoginLogo(theme: string): void {
    this.loginLogo = theme === 'dark' ? 'assets/VOWEL12.png' : 'assets/Icon-256.png';
  }

  private getLastLogin(username: string): string | null {
    if (!username) {
      return null;
    }
    try {
      const map = JSON.parse(localStorage.getItem(LoginComponent.LAST_LOGIN_KEY) || '{}');
      const iso = map[username.toLowerCase().trim()];
      return iso ? new Date(iso).toLocaleString() : null;
    } catch {
      return null;
    }
  }

  private recordLogin(username: string): void {
    if (!username) {
      return;
    }
    try {
      const map = JSON.parse(localStorage.getItem(LoginComponent.LAST_LOGIN_KEY) || '{}');
      map[username.toLowerCase().trim()] = new Date().toISOString();
      localStorage.setItem(LoginComponent.LAST_LOGIN_KEY, JSON.stringify(map));
    } catch {
      // localStorage unavailable (private mode, etc.) — skips silently
    }
  }

  onSubmit(): void {
    if (this.loginForm.invalid) {
      return;
    }
    this.fcmToken = sessionStorage.getItem('fcmToken') || '';
    this.loader = true;
    this.messagingService.requestPermission().catch(() => { this.fcmToken = ''; });
    const deatils: LoginCredentialModel = this.loginForm.value;
    deatils.fcmToken = this.fcmToken;
    this.subscribe.push(this.loginService.login(deatils).subscribe(
      data => {
        this.loader = false;
        const wrapper: LoginModel = JSON.parse(JSON.stringify(data));
        sessionStorage.clear();
        if (wrapper.role === 'DEVICE_ADMIN') {
          if (wrapper.mobileMode === null || wrapper.mobileMode === undefined || wrapper.mobileMode === 'mobile') {
            this.toaster.error('You do not have permission to login.');
            return false;
          }
          sessionStorage.setItem('flatId', wrapper.flatId.toString());
        }
        const store = (key: string, value: any) => {
          if (value !== null && value !== undefined && value !== 'undefined' && value !== 'null') {
            sessionStorage.setItem(key, value.toString());
          }
        };
        store('token', wrapper.id_token);
        store('accountName', wrapper.accountName);
        store('username', this.loginForm.value.username);
        store('type', wrapper.type);
        store('role', wrapper.role);
        store('dialingCode', wrapper.dialingCode);
        store('county', wrapper.county);
        store('country', wrapper.country);
        store('bindingEligible', wrapper.bindingEligible ? 'true' : 'false');
        store('licenseStatus', wrapper.licenseStatus ? wrapper.licenseStatus : 'VALID');

        if (wrapper.role === 'RESIDENT_ADMIN' || wrapper.role === 'FAMILY') {
          sessionStorage.setItem('residentId', wrapper.residentId.toString());
          sessionStorage.setItem('flatId', wrapper.flatId.toString());
          sessionStorage.setItem('hostCheckOut', wrapper.hostCheckOut.toString());
        }
        const accountIdNum = Number(wrapper.accountId);
        if (accountIdNum > 0) {
          sessionStorage.setItem('accountId', accountIdNum.toString());
          if (wrapper.userMasterId !== null && wrapper.userMasterId !== undefined) {
            sessionStorage.setItem('userMasterId', wrapper.userMasterId.toString());
          }
          sessionStorage.setItem('userImage', wrapper.image ? wrapper.image : '');
        }
        if (wrapper.role === 'EMPLOYEE_ADMIN') {
          sessionStorage.setItem('employeeId', wrapper.employeeId.toString());
          sessionStorage.setItem('hostCheckOut', wrapper.hostCheckOut.toString());
        }
        if (wrapper.role === 'ACCOUNT_ADMIN') {
          sessionStorage.setItem('flatId', wrapper.flatId.toString());
        }
        this.welcomeName = this.capitalize(this.loginForm.value.username);
        this.welcomeRole = LoginComponent.ROLE_LABELS[wrapper.role] || wrapper.role;
        this.welcomeBgImage = LoginComponent.ROLE_BG_IMAGES[wrapper.role] || '';
        this.loadAccountImage(wrapper.accountImage);
        this.recordLogin(this.loginForm.value.username);
        this.setRememberedUsername(this.loginForm.value.username, !!this.loginForm.value.rememberMe);
        this.showSuccessOverlay = true;
        setTimeout(() => this.route.navigate(['/home/dashboard']), 1700);
      }, error => {
        this.loader = false;
        this.commonMethod.returnErrorMessage(error);
        sessionStorage.clear();
        if (error.status === 401 && error.error && error.error.detail === 'Bad credentials') {
          this.triggerShake();
        }
      }));
  }

  private loadAccountImage(accountImage: string): void {
    this.accountImageUrl = '';
    if (!accountImage || accountImage.trim() === '') {
      return;
    }
    if (accountImage.startsWith('data:')) {
      this.accountImageUrl = accountImage;
      return;
    }
    let cached: string | null = null;
    try {
      cached = sessionStorage.getItem('vw-account-image:' + accountImage);
    } catch {
      cached = null;
    }
    if (cached) {
      this.accountImageUrl = cached;
      return;
    }
    this.subscribe.push(this.userService.getImageBase64(accountImage).subscribe(
      (res: any) => {
        const dataUrl = 'data:image/jpg;base64,' + res.imageBase64;
        this.accountImageUrl = dataUrl;
        try {
          sessionStorage.setItem('vw-account-image:' + accountImage, dataUrl);
        } catch {
          // sessionStorage unavailable (private mode, quota, etc.) — skip silently
        }
      },
      () => {
        // Keep the generic role-based background on failure.
      }
    ));
  }

  private triggerShake(): void {
    this.shakeError = false;
    requestAnimationFrame(() => {
      this.shakeError = true;
      setTimeout(() => (this.shakeError = false), 650);
    });
  }

  private capitalize(value: string): string {
    if (!value) {
      return '';
    }
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  forgetPassword(): void {
    this.route.navigate(['/forget-password']);
  }

  signUp(): void {
    this.route.navigate(['/sign-up']);
  }

  ngOnDestroy(): void {
    this.subscribe.forEach(destroyExistingValue => {
      destroyExistingValue.unsubscribe();
    });
  }
}
